import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const toComparableDueDate = (value) => {
  if (!value) {
    return null;
  }

  const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;
  if (dateOnlyPattern.test(String(value))) {
    const localEndOfDay = new Date(`${value}T23:59:59`);
    return Number.isNaN(localEndOfDay.getTime()) ? null : localEndOfDay;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalItems: 0,
    issuedItems: 0,
    availableItems: 0,
    activeStudents: 0,
    lateReturns: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [itemsRes, studentsRes, issuedRes] = await Promise.all([
        axios.get('/api/items/stats/overview'),
        axios.get('/api/students/stats/count'),
        axios.get('/api/transactions/issued')
      ]);

      const totalItems = Number(itemsRes.data.totalItems) || 0;
      const rawAvailable = Number(itemsRes.data.availableItems) || 0;
      const availableItems = Math.min(totalItems, Math.max(0, rawAvailable));
      const issuedItems = Math.max(0, totalItems - availableItems);

      const now = new Date();
      const overdueRecordsCount = (issuedRes.data || []).filter((issue) => {
        const returned = issue.returned === true || issue.status === 'returned';
        const expectedDateRaw = issue.expectedReturnDate || issue.expected_return_date || issue.return_date;
        const expectedDate = toComparableDueDate(expectedDateRaw);

        if (returned || !expectedDate) {
          return false;
        }

        return expectedDate < now;
      }).length;

      setStats({
        totalItems,
        issuedItems,
        availableItems,
        activeStudents: studentsRes.data.activeStudents || 0,
        lateReturns: overdueRecordsCount
      });
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>;
  }

  return (
    <div>
      <h1>Dashboard</h1>
      <div className="dashboard">
        <div className="card success">
          <div className="card-title">Total Equipment</div>
          <div className="card-value">{stats.totalItems}</div>
        </div>

        <div className="card info">
          <div className="card-title">Available Equipment</div>
          <div className="card-value">{stats.availableItems}</div>
        </div>

        <div className="card warning">
          <div className="card-title">Issued Equipment</div>
          <div className="card-value">{stats.issuedItems}</div>
        </div>

        <div className="card danger">
          <div className="card-title">Active Students</div>
          <div className="card-value">{stats.activeStudents}</div>
        </div>

        <div
          className="card overdue card-clickable"
          role="button"
          tabIndex={0}
          title="Overdue items"
          onClick={() => navigate('/return')}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              navigate('/return');
            }
          }}
        >
          <div className="card-title">Late Returns</div>
          <div className="card-value">{stats.lateReturns}</div>
        </div>
      </div>

      <div style={{ marginTop: '40px', textAlign: 'center', color: '#7f8c8d' }}>
        <p>Welcome to Sports Equipment Management System</p>
        <p>Use the menu to manage students, equipment, and transactions</p>
      </div>
    </div>
  );
};

export default Dashboard;
